import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import baseAPIService from '../services/baseapiservice';

interface RouteItem {
  mnu_itm_nm?: string;
  mnu_itm_url_tx?: string;
  stp_opt_url_tx?: string;
  rpt_url_tx?: string;
  children?: RouteItem[];
}

interface RouteCheckResult {
  allowed: boolean;
  error?: string;
}

interface RouteContextType {
  allowedRoutes: string[];
  isLoading: boolean;
  error: string | null;
  isRouteAllowed: (path: string) => boolean;
  refreshRoutes: () => Promise<void>;
  checkSetupAccess: (url: string, currentPath: string) => Promise<RouteCheckResult>;
  checkReportAccess: (reportId: number) => Promise<RouteCheckResult>;
}

const RouteContext = createContext<RouteContextType | undefined>(undefined);

export const useRouteAccess = () => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error('useRouteAccess must be used within RouteProvider');
  }
  return context;
};

interface RouteProviderProps {
  children: ReactNode;
}

export const RouteProvider: React.FC<RouteProviderProps> = ({ children }) => {
  const [allowedRoutes, setAllowedRoutes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkedSetupUrls, setCheckedSetupUrls] = useState<Set<string>>(new Set());
  const [checkedReportIds, setCheckedReportIds] = useState<Set<number>>(new Set());
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('x-access-token'));

  /**
   * Extract all URLs from hierarchical structure (recursive)
   */
  const extractUrls = useCallback((items: RouteItem[]): string[] => {
    const urls: string[] = [];

    const traverse = (item: RouteItem, depth: number = 0) => {
      // Extract URL from different possible field names
      const url = item.mnu_itm_url_tx || item.stp_opt_url_tx || item.rpt_url_tx;
      const name = item.mnu_itm_nm;

      // Only add if it's a real route (not # or empty)
      if (url && url !== '#' && url.startsWith('/')) {
        // console.log(`[extractUrls] ${'  '.repeat(depth)}Found route:`, { name, url });
        urls.push(url);
      }

      // Recursively process children
      if (item.children) {
        item.children.forEach(child => traverse(child, depth + 1));
      }
    };

    items.forEach(item => traverse(item, 0));
    // console.log('[extractUrls] Total URLs extracted:', urls.length);
    return urls;
  }, []);

  /**
   * Fetch accessible routes from backend
   */
  const fetchAccessibleRoutes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const authType = localStorage.getItem('auth_type');
      const authEndpoint = authType === 'crda' ? 'crda-auth' :
        authType === 'apufidc' ? 'apufidc-auth' :
          'erp-auth';

      const response = await baseAPIService.get(`${authEndpoint}/menu`);
      // console.log('sony', response)
      const data = response.data?.data || [];

      // Extract all allowed URLs from hierarchical structure
      const urls = extractUrls(data);

      // Add common public routes that don't need explicit permission
      const publicRoutes = [
        '/app/profile',
        '/app/help',
        '/app/themes',
        '/app/support-ticket',
        '/app/dashboard',
        '/',
        '/login',
        '/signup',
        '/forgot-password',
        '/documentation'
      ];

      setAllowedRoutes([...urls, ...publicRoutes]);
      setError(null);

      // console.log('[RouteContext] Loaded', urls.length, 'accessible routes from backend');
      // console.log('[RouteContext] Extracted URLs:', urls);

    } catch (err: any) {
      console.error('[RouteContext] Error fetching accessible routes:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load menu. Please try again.';
      setError(errorMessage);
      // Set only public routes on error
      setAllowedRoutes([
        '/app/profile',
        '/app/help',
        '/app/dashboard',
        '/',
        '/login',
        '/signup',
        '/forgot-password'
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [extractUrls]);

  /**
   * Check if route is allowed (exact match only, no prefix matching)
   */
  const isRouteAllowed = useCallback((currentPath: string): boolean => {
    // Remove query params and hash
    const cleanPath = currentPath.split('?')[0].split('#')[0];

    const matched = allowedRoutes.some(allowedRoute => {
      // Exact match only
      if (cleanPath === allowedRoute) {
        return true;
      }

      // Pattern match (for dynamic routes like /reports/:id)
      if (allowedRoute.includes(':')) {
        const pattern = allowedRoute.replace(/:\w+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        if (regex.test(cleanPath)) {
          return true;
        }
      }

      return false;
    });

    return matched;
  }, [allowedRoutes]);

  /**
   * Check setup route access (lazy loaded)
   * Returns result with allowed status and optional error message
   */
  const checkSetupAccess = useCallback(async (url: string, currentPath: string): Promise<RouteCheckResult> => {
    // Check cache first - if we've already checked this setup URL, don't check again
    if (checkedSetupUrls.has(url)) {
      console.log('[RouteContext] ✅ Cache hit for setup URL:', url);
      // Already checked, just verify if current path is in allowed routes
      const matchedUrl = allowedRoutes.find(allowedRoute => {
        if (currentPath === allowedRoute) return true;
        if (currentPath.startsWith(allowedRoute + '/')) return true;
        return false;
      });
      console.log('[RouteContext] Cache check result:', { currentPath, matchedUrl, allowed: !!matchedUrl });
      return { allowed: !!matchedUrl };
    }

    console.log('[RouteContext] ⚠️ Cache miss for setup URL, fetching from API:', url);

    try {
      const authType = localStorage.getItem('auth_type');
      const authEndpoint = authType === 'crda' ? 'crda-auth' :
        authType === 'apufidc' ? 'apufidc-auth' :
          'erp-auth';

      const response = await baseAPIService.get(`${authEndpoint}/menu/setup`, {
        params: { url }
      });

      const setupData = response.data?.data || [];

      // Extract setup URLs and add to allowed routes
      const setupUrls: string[] = [];
      setupData.forEach((group: any) => {
        group.options?.forEach((opt: any) => {
          if (opt.stp_opt_url_tx) {
            setupUrls.push(opt.stp_opt_url_tx);
          }
        });
      });

      // Mark this setup URL as checked
      setCheckedSetupUrls(prev => new Set([...prev, url]));

      // Cache the setup URLs
      if (setupUrls.length > 0) {
        setAllowedRoutes(prev => [...new Set([...prev, ...setupUrls])]);
        console.log('[RouteContext] Added', setupUrls.length, 'setup routes to cache');
      }

      // Check if the current path matches any of the loaded setup URLs
      const matchedUrl = setupUrls.find(setupUrl => {
        // Exact match
        if (currentPath === setupUrl) return true;
        // Prefix match
        if (currentPath.startsWith(setupUrl + '/')) return true;
        return false;
      });

      return { allowed: !!matchedUrl };
    } catch (error: any) {
      console.error('[RouteContext] Error checking setup access:', error);
      // Don't mark as checked on error - allow retry
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load setup options. Please try again.';
      return { allowed: false, error: errorMessage };
    }
  }, [checkedSetupUrls, allowedRoutes]);

  /**
   * Check report route access (lazy loaded)
   * Returns result with allowed status and optional error message
   */
  const checkReportAccess = useCallback(async (reportId: number): Promise<RouteCheckResult> => {
    // Check cache first - if we've already checked this report, don't check again
    if (checkedReportIds.has(reportId)) {
      // Already checked, just verify if report URL is in allowed routes
      const reportUrl = `/app/maud-erp/reports/${reportId}`;
      return { allowed: allowedRoutes.includes(reportUrl) };
    }

    try {
      const authType = localStorage.getItem('auth_type');
      const authEndpoint = authType === 'crda' ? 'crda-auth' :
        authType === 'apufidc' ? 'apufidc-auth' :
          'erp-auth';

      const response = await baseAPIService.get(`${authEndpoint}/menu/reports`);

      const reportsData = response.data?.data || [];

      // Check if report exists in accessible reports
      const hasAccess = reportsData.some((report: any) => report.rpt_id === reportId);

      // Mark this report as checked
      setCheckedReportIds(prev => new Set([...prev, reportId]));

      if (hasAccess) {
        // Cache the report URL
        const reportUrl = `/app/maud-erp/reports/${reportId}`;
        setAllowedRoutes(prev => [...prev, reportUrl]);
        console.log('[RouteContext] Added report', reportId, 'to accessible routes');
      }

      return { allowed: hasAccess };
    } catch (error: any) {
      console.error('[RouteContext] Error checking report access:', error);
      // Don't mark as checked on error - allow retry
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load reports. Please try again.';
      return { allowed: false, error: errorMessage };
    }
  }, [checkedReportIds, allowedRoutes]);

  /**
   * Refresh routes (called after role/tenant change)
   */
  const refreshRoutes = useCallback(async () => {
    console.log('[RouteContext] Refreshing accessible routes');
    // Clear caches
    setCheckedSetupUrls(new Set());
    setCheckedReportIds(new Set());
    await fetchAccessibleRoutes();
  }, [fetchAccessibleRoutes]);

  // Monitor localStorage changes for token updates (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('x-access-token');
      setAuthToken(token);
    };

    // Listen for storage changes (works across tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes in the same tab (storage event doesn't fire in same tab)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch routes only when user is authenticated (has token)
  useEffect(() => {
    if (authToken) {
      fetchAccessibleRoutes();
    } else {
      // User not authenticated, set loading to false and only allow public routes
      setIsLoading(false);
      setAllowedRoutes([
        '/',
        '/login',
        '/signup',
        '/forgot-password',
        '/documentation'
      ]);
    }
  }, [authToken, fetchAccessibleRoutes]);

  const value: RouteContextType = {
    allowedRoutes,
    isLoading,
    error,
    isRouteAllowed,
    refreshRoutes,
    checkSetupAccess,
    checkReportAccess
  };

  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
};
